import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useQuoteLocale } from '@/quote/hooks/useQuoteLocale';
import { judgeWhetherUseMultilingual } from '@/quote/util/multilingual';
import { QuoteRecoverySourceEnum } from '@jove-frontend/enums';
import { generateUniversalMuiTheme } from '@jove-frontend/mui';
import '@jove-frontend/themes';
import { Button, LoadingMask, handleQuotePostMessage, supportTranslationCountryConvertMap, useSessionStorage } from '@jove-frontend/uikit';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { StateMachineProvider, createStore } from 'little-state-machine';
import { LinkedInInsightTag } from 'nextjs-linkedin-insight-tag';
import NProgress from 'nprogress';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import 'react-calendar/dist/Calendar.css';
import { Toaster } from 'react-hot-toast';
import 'react-phone-number-input/style.css';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AnalyticsProvider } from 'use-analytics';
import EventsTriggerWrapper from '../components/EventsTriggerWrapper';
import { initialI18n } from '../i18n/index';
import '../styles/globals.css';
import { analytics } from '../util/analytics';
import { changeBodyBackgroundColor } from '../util/global';

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
  posthog.init('phc_GWMy0ZDzSKuoYUOmo7411DMtmyehnTpx4uQFY4mHhLu', {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',

    loaded: (posthog) => {
      // Disable in development and staging
      if (process.env.NODE_ENV === 'development' || process.env.STAGING === 'true') posthog.opt_out_capturing();
    },
  });
  // Disable in development and staging
  if (!(process.env.NODE_ENV === 'development' || process.env.STAGING === 'true')) {
    Sentry.addIntegration(
      new posthog.SentryIntegration(posthog, 'https://sentry.io/organizations/jove-insurance/projects/', 4504516236541952, undefined, [
        'error',
        'warning',
        'fatal',
      ])
    );
  }
}

function logStore(store) {
  // if is not production, log the store
  if (process.env.NODE_ENV !== 'production') {
    console.debug(store);
    return store;
  }
}

createStore(
  {
    contact: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      country: '',
      organization_id: '',
    },
    business: {
      type: undefined,
      businessActivity: '',
      activities: [''],
      turnover: '',
      name: '',
      number: '',
      address: '',
      address2: '',
      postcode: '',
      employees: 0,
      country: '',
      UKOnlyCustomers: false,
      turnoverBreakdownEU: 0,
      turnoverBreakdownOther: 0,
      turnoverBreakdownUK: 0,
      turnoverBreakdownUSA: 0,
      employeesSplit: {
        clerical: 0,
        manual: 0,
      },
    },
    ern: '',
    quote: {
      id: '',
      created_at: '',
      updated_at: '',
      viewed_at: '',
      notified: false,
    },
    projectPeriodStart: '',
    projectPeriodEnd: '',
    company: {
      name: '',
      registration_num: '',
      tax_num: '',
      company_incorporation_date: undefined,
    },
    billingCycle: 'annual',
    quoteRecoverySource: QuoteRecoverySourceEnum.Normal,
    greateAmerican: {},
    currency: {
      code: 'GBP',
      symbol: '£',
    },
    declarationQuestions: {},
    promotionId: '',
    coverageConfiguration: null,
  },
  {
    name: 'jove-quote',
    middleWares: [logStore],
    // storageType: typeof window !== 'undefined' ? window.sessionStorage : null,
    persist: 'action',
  }
);

const ErrorFallback = () => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>

      <Button onClick={() => Sentry.showReportDialog({})}>Report feedback</Button>
    </div>
  );
};

const APP_WIDE_LAYOUT_ROUTE_LIST = ['/application-form/[quoteId]'];

function JoveQuote({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [isApplicationFormRoute, setIsApplicationFormRoute] = useState(false);
  const [theme, setTheme] = useSessionStorage('theme', 'default');
  const [queryClient] = useState(() => new QueryClient());
  const [renderFlag, setRenderFlag] = useState(true);
  const { getCurrentUseCountryCode } = useQuoteLocale();

  useEffect(() => {
    initialProjectInfo();

    try {
      NProgress.configure({ parent: '#jove-quote' });
    } catch (_) {}
    // Track page views
    const handleRouteChange = () => {
      posthog?.capture('$pageview');
      analytics.page();
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    window?.addEventListener('message', handleQuotePostMessage);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      window?.removeEventListener('message', handleQuotePostMessage);
    };
  }, []);

  useEffect(() => {
    const handleStart = (url) => {
      try {
        NProgress.start();
      } catch (_) {}
    };
    const handleStop = () => {
      try {
        NProgress.done();
      } catch (_) {}
    };

    updateBodyBackgroundColor();

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  // Set theme to sessionStorage
  useEffect(() => {
    const currentTheme = router.query.theme || router.query.channel || theme;

    if (theme) {
      setTheme(currentTheme as string);

      document.querySelector('html').setAttribute('data-theme', currentTheme as string);
    }
  }, [router.query.theme, router.query.channel]);

  // Set ref to sessionStorage
  useEffect(() => {
    const ref = router.query.ref || (typeof window !== 'undefined' && window.sessionStorage.getItem('jove-ref')) || 'default';

    if (ref) {
      window.sessionStorage.setItem('jove-ref', ref as string);
      // add ref to data-ref attribute on main
      document.querySelector('html').setAttribute('data-ref', ref as string);
    }
  }, [router.query.ref]);

  useEffect(() => {
    setIsApplicationFormRoute(APP_WIDE_LAYOUT_ROUTE_LIST.includes(router.pathname));
  }, [router]);

  /**
   * methods
   */
  const initialProjectInfo = async () => {
    await initialI18nInfo();
    setRenderFlag(false);
  };

  const initialI18nInfo = async () => {
    let lng = 'en';
    if (judgeWhetherUseMultilingual()) {
      const useCountryCode = await getCurrentUseCountryCode();
      try {
        lng = supportTranslationCountryConvertMap[useCountryCode]?.locale || supportTranslationCountryConvertMap.DEFAULT.locale;
      } catch (e) {}
    }
    initialI18n(lng);
  };

  const updateBodyBackgroundColor = () => {
    if (sessionStorage.getItem('jove-body-background-color')) {
      changeBodyBackgroundColor(sessionStorage.getItem('jove-body-background-color'));
    }
  };

  if (!router.isReady || renderFlag) {
    return <LoadingMask />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient} contextSharing={true}>
        <AnalyticsProvider instance={analytics}>
          <PostHogProvider client={posthog}>
            <ThemeProvider theme={generateUniversalMuiTheme()}>
              <StateMachineProvider>
                <EventsTriggerWrapper>
                  <Head>
                    <title>Jove - Flexible packaged insurance for self-employed people and small business owners</title>
                  </Head>
                  <main
                    className={clsx(
                      isApplicationFormRoute
                        ? 'app min-h-screen w-full bg-[#f2f3f8] antialiased print:bg-white'
                        : 'app mx-auto flex h-full w-full justify-center antialiased sm:w-max sm:items-center'
                    )}
                  >
                    <div
                      id="jove-quote"
                      data-quote-chat-anchor
                      className={clsx(
                        isApplicationFormRoute
                          ? 'relative min-h-screen w-full bg-[#f2f3f8]'
                          : 'flex-center relative h-full w-full flex-shrink-0 bg-white sm:max-h-[840px] sm:w-[414px] sm:overflow-y-auto sm:shadow-md'
                      )}
                    >
                      <LinkedInInsightTag partnerId={process.env.NX_LINKEDIN_PARTNER_ID} />
                      <Component key={router.asPath} {...pageProps} />
                    </div>
                    <Toaster
                      toastOptions={{
                        duration: 2500,
                      }}
                    />
                  </main>
                </EventsTriggerWrapper>
              </StateMachineProvider>
            </ThemeProvider>
          </PostHogProvider>
        </AnalyticsProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default JoveQuote;
